import React, { useState } from "react";
import { getCategoryGuidance } from "../lib/categoryGuidance";
import { CheckCircle2, XCircle, Info, ChevronDown, ChevronUp, BookOpen } from "lucide-react";

export default function CategoryGuidanceCard({ category, defaultExpanded = false, className = "" }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const guidance = getCategoryGuidance(category);

  if (!guidance) return null;

  return (
    <div className={`border border-blue-100 dark:border-blue-900/40 bg-blue-50/50 dark:bg-blue-950/20 rounded-xl p-3 text-xs text-slate-700 dark:text-slate-300 transition-all ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="p-1 rounded-md bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300">
            <BookOpen className="w-3.5 h-3.5" />
          </span>
          <span className="font-semibold text-slate-800 dark:text-slate-100 truncate">
            {guidance.title}
          </span>
          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
            {guidance.scheduleELine}
          </span>
          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300">
            Deductible: {guidance.typicalPct}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 text-[11px] font-medium flex items-center gap-1 shrink-0 p-1"
        >
          <span>{expanded ? "Less" : "Tax Rules"}</span>
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>

      <p className="mt-1.5 text-[11.5px] leading-relaxed text-slate-600 dark:text-slate-400">
        {guidance.summary}
      </p>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-blue-100 dark:border-blue-900/30 space-y-2.5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            <div className="bg-white/80 dark:bg-slate-900/70 p-2.5 rounded-lg border border-emerald-100 dark:border-emerald-950/30">
              <div className="font-semibold text-emerald-700 dark:text-emerald-400 flex items-center gap-1 mb-1.5 text-[11px]">
                <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                <span>What Qualifies</span>
              </div>
              <ul className="space-y-1">
                {guidance.qualifies.map((q, idx) => (
                  <li key={idx} className="flex items-start gap-1.5 text-[11px] text-slate-600 dark:text-slate-400 leading-snug">
                    <span className="text-emerald-500 font-bold">•</span>
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white/80 dark:bg-slate-900/70 p-2.5 rounded-lg border border-rose-100 dark:border-rose-950/30">
              <div className="font-semibold text-rose-700 dark:text-rose-400 flex items-center gap-1 mb-1.5 text-[11px]">
                <XCircle className="w-3.5 h-3.5 shrink-0" />
                <span>Does NOT Qualify</span>
              </div>
              <ul className="space-y-1">
                {guidance.doesNotQualify.map((nq, idx) => (
                  <li key={idx} className="flex items-start gap-1.5 text-[11px] text-slate-600 dark:text-slate-400 leading-snug">
                    <span className="text-rose-500 font-bold">•</span>
                    <span>{nq}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {guidance.auditTips && (
            <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/40 p-2 rounded-lg text-amber-900 dark:text-amber-200 text-[11px] leading-snug">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
              <div>
                <span className="font-semibold">IRS Rule & Audit Tip: </span>
                <span>{guidance.auditTips}</span>
                <div className="text-[10px] text-amber-700/80 dark:text-amber-400/80 mt-0.5 font-mono">
                  Reference: {guidance.irsRef}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
